// Static country table for <PhoneInput />.
//
// ~220 entries keeping gzip under 10 KB. Flag emoji is derived from
// the ISO-2 code at runtime via regionalIndicator offsets — no image
// assets. Country names are English fallbacks; the component prefers
// `Intl.DisplayNames` at runtime so Japanese / Spanish viewers see
// localised names without us shipping 250 × 40-locale JSON.
//
// POPULAR countries appear in a pinned top cluster in the dropdown.
// Hand-picked for initial launch — we can reseed from actual signup
// analytics later.
//
// Countries whose dial code is in DEFAULT_CONFIG.otp.blocked_prefixes
// (src/@common/system-config.mjs) are deliberately excluded from this
// list. Showing them in the picker would be a foot-gun — a user could
// select them cleanly and only discover the block at OTP dispatch
// time. The paste-with-plus flow on PhoneInput does accept pasted
// numbers that start with blocked prefixes so we can surface the
// server-side reason ("This region is not currently supported…")
// verbatim, rather than silently dropping the input.

export interface Country {
  iso2: string;
  name: string; // English fallback when Intl.DisplayNames is unavailable
  dial: string; // Dial code without the leading '+'
}

export const POPULAR_ISO2: ReadonlySet<string> = new Set([
  'ES', 'US', 'GB', 'FR', 'DE', 'MX', 'BR', 'IN', 'CA', 'AU',
]);

// Blocked dial prefixes must stay in lock-step with the defaults in
// src/@common/system-config.mjs::DEFAULT_CONFIG.otp.blocked_prefixes.
// Kept here as a constant so the filter below is auditable in one
// place. A shared source between backend + frontend would be nicer
// long-term but crosses the src/ ↔ frontend/ boundary.
const BLOCKED_DIAL_PREFIXES: ReadonlySet<string> = new Set([
  '216', '252', '373', '375', '253', '228', '244', '249', '970', '998',
]);

// Seed list — ITU-T E.164 country codes covering sovereign states,
// inhabited overseas territories (Guadeloupe / Réunion / Greenland /
// Hong Kong …), and the NANP Caribbean members that share +1 with
// the US/Canada. Rare uninhabited codes (Antarctica, Heard Island,
// Bouvet) are handled via TERRITORY_FALLBACK below — CloudFront can
// return those ISO-2s but they'd clutter the picker.
const SEED: ReadonlyArray<Country> = [
  // North America (+1 shared)
  { iso2: 'US', name: 'United States', dial: '1' },
  { iso2: 'CA', name: 'Canada', dial: '1' },
  // US territories on the NANP (+1)
  { iso2: 'AS', name: 'American Samoa', dial: '1' },
  { iso2: 'GU', name: 'Guam', dial: '1' },
  { iso2: 'MP', name: 'Northern Mariana Islands', dial: '1' },
  { iso2: 'VI', name: 'U.S. Virgin Islands', dial: '1' },
  // NANP Caribbean (+1 shared)
  { iso2: 'AG', name: 'Antigua and Barbuda', dial: '1' },
  { iso2: 'AI', name: 'Anguilla', dial: '1' },
  { iso2: 'BB', name: 'Barbados', dial: '1' },
  { iso2: 'BM', name: 'Bermuda', dial: '1' },
  { iso2: 'BS', name: 'Bahamas', dial: '1' },
  { iso2: 'DM', name: 'Dominica', dial: '1' },
  { iso2: 'GD', name: 'Grenada', dial: '1' },
  { iso2: 'JM', name: 'Jamaica', dial: '1' },
  { iso2: 'KN', name: 'Saint Kitts and Nevis', dial: '1' },
  { iso2: 'KY', name: 'Cayman Islands', dial: '1' },
  { iso2: 'LC', name: 'Saint Lucia', dial: '1' },
  { iso2: 'MS', name: 'Montserrat', dial: '1' },
  { iso2: 'SX', name: 'Sint Maarten', dial: '1' },
  { iso2: 'TC', name: 'Turks and Caicos Islands', dial: '1' },
  { iso2: 'TT', name: 'Trinidad and Tobago', dial: '1' },
  { iso2: 'VC', name: 'Saint Vincent and the Grenadines', dial: '1' },
  { iso2: 'VG', name: 'British Virgin Islands', dial: '1' },
  // Europe
  { iso2: 'AD', name: 'Andorra', dial: '376' },
  { iso2: 'AL', name: 'Albania', dial: '355' },
  { iso2: 'AT', name: 'Austria', dial: '43' },
  { iso2: 'BA', name: 'Bosnia and Herzegovina', dial: '387' },
  { iso2: 'BE', name: 'Belgium', dial: '32' },
  { iso2: 'BG', name: 'Bulgaria', dial: '359' },
  { iso2: 'CH', name: 'Switzerland', dial: '41' },
  { iso2: 'CY', name: 'Cyprus', dial: '357' },
  { iso2: 'CZ', name: 'Czechia', dial: '420' },
  { iso2: 'DE', name: 'Germany', dial: '49' },
  { iso2: 'DK', name: 'Denmark', dial: '45' },
  { iso2: 'EE', name: 'Estonia', dial: '372' },
  { iso2: 'ES', name: 'Spain', dial: '34' },
  { iso2: 'FI', name: 'Finland', dial: '358' },
  { iso2: 'FR', name: 'France', dial: '33' },
  { iso2: 'GB', name: 'United Kingdom', dial: '44' },
  { iso2: 'GR', name: 'Greece', dial: '30' },
  { iso2: 'HR', name: 'Croatia', dial: '385' },
  { iso2: 'HU', name: 'Hungary', dial: '36' },
  { iso2: 'IE', name: 'Ireland', dial: '353' },
  { iso2: 'IS', name: 'Iceland', dial: '354' },
  { iso2: 'IT', name: 'Italy', dial: '39' },
  { iso2: 'LI', name: 'Liechtenstein', dial: '423' },
  { iso2: 'LT', name: 'Lithuania', dial: '370' },
  { iso2: 'LU', name: 'Luxembourg', dial: '352' },
  { iso2: 'LV', name: 'Latvia', dial: '371' },
  { iso2: 'MC', name: 'Monaco', dial: '377' },
  { iso2: 'ME', name: 'Montenegro', dial: '382' },
  { iso2: 'MK', name: 'North Macedonia', dial: '389' },
  { iso2: 'MT', name: 'Malta', dial: '356' },
  { iso2: 'NL', name: 'Netherlands', dial: '31' },
  { iso2: 'NO', name: 'Norway', dial: '47' },
  { iso2: 'PL', name: 'Poland', dial: '48' },
  { iso2: 'PT', name: 'Portugal', dial: '351' },
  { iso2: 'RO', name: 'Romania', dial: '40' },
  { iso2: 'RS', name: 'Serbia', dial: '381' },
  { iso2: 'RU', name: 'Russia', dial: '7' },
  { iso2: 'SE', name: 'Sweden', dial: '46' },
  { iso2: 'SI', name: 'Slovenia', dial: '386' },
  { iso2: 'SK', name: 'Slovakia', dial: '421' },
  { iso2: 'SM', name: 'San Marino', dial: '378' },
  { iso2: 'TR', name: 'Turkey', dial: '90' },
  { iso2: 'UA', name: 'Ukraine', dial: '380' },
  { iso2: 'VA', name: 'Vatican City', dial: '379' },
  // European overseas / crown dependencies
  { iso2: 'AX', name: 'Åland Islands', dial: '358' },
  { iso2: 'FO', name: 'Faroe Islands', dial: '298' },
  { iso2: 'GG', name: 'Guernsey', dial: '44' },
  { iso2: 'GI', name: 'Gibraltar', dial: '350' },
  { iso2: 'GL', name: 'Greenland', dial: '299' },
  { iso2: 'IM', name: 'Isle of Man', dial: '44' },
  { iso2: 'JE', name: 'Jersey', dial: '44' },
  { iso2: 'XK', name: 'Kosovo', dial: '383' },

  // Latin America
  { iso2: 'AR', name: 'Argentina', dial: '54' },
  { iso2: 'BO', name: 'Bolivia', dial: '591' },
  { iso2: 'BR', name: 'Brazil', dial: '55' },
  { iso2: 'CL', name: 'Chile', dial: '56' },
  { iso2: 'CO', name: 'Colombia', dial: '57' },
  { iso2: 'CR', name: 'Costa Rica', dial: '506' },
  { iso2: 'CU', name: 'Cuba', dial: '53' },
  { iso2: 'DO', name: 'Dominican Republic', dial: '1' },
  { iso2: 'EC', name: 'Ecuador', dial: '593' },
  { iso2: 'GT', name: 'Guatemala', dial: '502' },
  { iso2: 'HN', name: 'Honduras', dial: '504' },
  { iso2: 'MX', name: 'Mexico', dial: '52' },
  { iso2: 'NI', name: 'Nicaragua', dial: '505' },
  { iso2: 'PA', name: 'Panama', dial: '507' },
  { iso2: 'PE', name: 'Peru', dial: '51' },
  { iso2: 'PR', name: 'Puerto Rico', dial: '1' },
  { iso2: 'PY', name: 'Paraguay', dial: '595' },
  { iso2: 'SV', name: 'El Salvador', dial: '503' },
  { iso2: 'UY', name: 'Uruguay', dial: '598' },
  { iso2: 'VE', name: 'Venezuela', dial: '58' },
  // Caribbean (non-NANP) + French overseas
  { iso2: 'AW', name: 'Aruba', dial: '297' },
  { iso2: 'BL', name: 'Saint Barthélemy', dial: '590' },
  { iso2: 'BQ', name: 'Caribbean Netherlands', dial: '599' },
  { iso2: 'CW', name: 'Curaçao', dial: '599' },
  { iso2: 'GF', name: 'French Guiana', dial: '594' },
  { iso2: 'GP', name: 'Guadeloupe', dial: '590' },
  { iso2: 'HT', name: 'Haiti', dial: '509' },
  { iso2: 'MF', name: 'Saint Martin', dial: '590' },
  { iso2: 'MQ', name: 'Martinique', dial: '596' },
  { iso2: 'PM', name: 'Saint Pierre and Miquelon', dial: '508' },
  { iso2: 'SR', name: 'Suriname', dial: '597' },

  // Asia-Pacific
  { iso2: 'AU', name: 'Australia', dial: '61' },
  { iso2: 'BD', name: 'Bangladesh', dial: '880' },
  { iso2: 'BN', name: 'Brunei', dial: '673' },
  { iso2: 'BT', name: 'Bhutan', dial: '975' },
  { iso2: 'CN', name: 'China', dial: '86' },
  { iso2: 'HK', name: 'Hong Kong', dial: '852' },
  { iso2: 'ID', name: 'Indonesia', dial: '62' },
  { iso2: 'IN', name: 'India', dial: '91' },
  { iso2: 'JP', name: 'Japan', dial: '81' },
  { iso2: 'KH', name: 'Cambodia', dial: '855' },
  { iso2: 'KR', name: 'South Korea', dial: '82' },
  { iso2: 'LA', name: 'Laos', dial: '856' },
  { iso2: 'LK', name: 'Sri Lanka', dial: '94' },
  { iso2: 'MM', name: 'Myanmar', dial: '95' },
  { iso2: 'MN', name: 'Mongolia', dial: '976' },
  { iso2: 'MO', name: 'Macau', dial: '853' },
  { iso2: 'MV', name: 'Maldives', dial: '960' },
  { iso2: 'MY', name: 'Malaysia', dial: '60' },
  { iso2: 'NP', name: 'Nepal', dial: '977' },
  { iso2: 'NZ', name: 'New Zealand', dial: '64' },
  { iso2: 'PH', name: 'Philippines', dial: '63' },
  { iso2: 'PK', name: 'Pakistan', dial: '92' },
  { iso2: 'SG', name: 'Singapore', dial: '65' },
  { iso2: 'TH', name: 'Thailand', dial: '66' },
  { iso2: 'TL', name: 'Timor-Leste', dial: '670' },
  { iso2: 'TW', name: 'Taiwan', dial: '886' },
  { iso2: 'VN', name: 'Vietnam', dial: '84' },
  // Pacific island nations + French overseas Pacific + Réunion
  { iso2: 'CK', name: 'Cook Islands', dial: '682' },
  { iso2: 'FJ', name: 'Fiji', dial: '679' },
  { iso2: 'FM', name: 'Micronesia', dial: '691' },
  { iso2: 'KI', name: 'Kiribati', dial: '686' },
  { iso2: 'MH', name: 'Marshall Islands', dial: '692' },
  { iso2: 'NC', name: 'New Caledonia', dial: '687' },
  { iso2: 'NR', name: 'Nauru', dial: '674' },
  { iso2: 'PF', name: 'French Polynesia', dial: '689' },
  { iso2: 'PG', name: 'Papua New Guinea', dial: '675' },
  { iso2: 'PW', name: 'Palau', dial: '680' },
  { iso2: 'RE', name: 'Réunion', dial: '262' },
  { iso2: 'SB', name: 'Solomon Islands', dial: '677' },
  { iso2: 'TO', name: 'Tonga', dial: '676' },
  { iso2: 'TV', name: 'Tuvalu', dial: '688' },
  { iso2: 'VU', name: 'Vanuatu', dial: '678' },
  { iso2: 'WF', name: 'Wallis and Futuna', dial: '681' },
  { iso2: 'WS', name: 'Samoa', dial: '685' },
  { iso2: 'YT', name: 'Mayotte', dial: '262' },

  // Middle East
  { iso2: 'AE', name: 'United Arab Emirates', dial: '971' },
  { iso2: 'BH', name: 'Bahrain', dial: '973' },
  { iso2: 'IL', name: 'Israel', dial: '972' },
  { iso2: 'IQ', name: 'Iraq', dial: '964' },
  { iso2: 'JO', name: 'Jordan', dial: '962' },
  { iso2: 'KW', name: 'Kuwait', dial: '965' },
  { iso2: 'LB', name: 'Lebanon', dial: '961' },
  { iso2: 'OM', name: 'Oman', dial: '968' },
  { iso2: 'QA', name: 'Qatar', dial: '974' },
  { iso2: 'SA', name: 'Saudi Arabia', dial: '966' },
  { iso2: 'SY', name: 'Syria', dial: '963' },
  { iso2: 'YE', name: 'Yemen', dial: '967' },

  // Central Asia & Caucasus
  { iso2: 'AF', name: 'Afghanistan', dial: '93' },
  { iso2: 'AM', name: 'Armenia', dial: '374' },
  { iso2: 'AZ', name: 'Azerbaijan', dial: '994' },
  { iso2: 'GE', name: 'Georgia', dial: '995' },
  { iso2: 'IR', name: 'Iran', dial: '98' },
  { iso2: 'KG', name: 'Kyrgyzstan', dial: '996' },
  { iso2: 'KP', name: 'North Korea', dial: '850' },
  { iso2: 'KZ', name: 'Kazakhstan', dial: '7' },
  { iso2: 'TJ', name: 'Tajikistan', dial: '992' },
  { iso2: 'TM', name: 'Turkmenistan', dial: '993' },

  // Africa
  { iso2: 'BF', name: 'Burkina Faso', dial: '226' },
  { iso2: 'BI', name: 'Burundi', dial: '257' },
  { iso2: 'BJ', name: 'Benin', dial: '229' },
  { iso2: 'BW', name: 'Botswana', dial: '267' },
  { iso2: 'CD', name: 'DR Congo', dial: '243' },
  { iso2: 'CF', name: 'Central African Republic', dial: '236' },
  { iso2: 'CG', name: 'Congo', dial: '242' },
  { iso2: 'CI', name: "Côte d'Ivoire", dial: '225' },
  { iso2: 'CM', name: 'Cameroon', dial: '237' },
  { iso2: 'CV', name: 'Cape Verde', dial: '238' },
  { iso2: 'DZ', name: 'Algeria', dial: '213' },
  { iso2: 'EG', name: 'Egypt', dial: '20' },
  { iso2: 'ER', name: 'Eritrea', dial: '291' },
  { iso2: 'ET', name: 'Ethiopia', dial: '251' },
  { iso2: 'GA', name: 'Gabon', dial: '241' },
  { iso2: 'GH', name: 'Ghana', dial: '233' },
  { iso2: 'GM', name: 'Gambia', dial: '220' },
  { iso2: 'GN', name: 'Guinea', dial: '224' },
  { iso2: 'GQ', name: 'Equatorial Guinea', dial: '240' },
  { iso2: 'GW', name: 'Guinea-Bissau', dial: '245' },
  { iso2: 'KE', name: 'Kenya', dial: '254' },
  { iso2: 'KM', name: 'Comoros', dial: '269' },
  { iso2: 'LR', name: 'Liberia', dial: '231' },
  { iso2: 'LS', name: 'Lesotho', dial: '266' },
  { iso2: 'LY', name: 'Libya', dial: '218' },
  { iso2: 'MA', name: 'Morocco', dial: '212' },
  { iso2: 'MG', name: 'Madagascar', dial: '261' },
  { iso2: 'ML', name: 'Mali', dial: '223' },
  { iso2: 'MR', name: 'Mauritania', dial: '222' },
  { iso2: 'MU', name: 'Mauritius', dial: '230' },
  { iso2: 'MW', name: 'Malawi', dial: '265' },
  { iso2: 'MZ', name: 'Mozambique', dial: '258' },
  { iso2: 'NA', name: 'Namibia', dial: '264' },
  { iso2: 'NE', name: 'Niger', dial: '227' },
  { iso2: 'NG', name: 'Nigeria', dial: '234' },
  { iso2: 'RW', name: 'Rwanda', dial: '250' },
  { iso2: 'SC', name: 'Seychelles', dial: '248' },
  { iso2: 'SL', name: 'Sierra Leone', dial: '232' },
  { iso2: 'SN', name: 'Senegal', dial: '221' },
  { iso2: 'SS', name: 'South Sudan', dial: '211' },
  { iso2: 'ST', name: 'São Tomé and Príncipe', dial: '239' },
  { iso2: 'SZ', name: 'Eswatini', dial: '268' },
  { iso2: 'TD', name: 'Chad', dial: '235' },
  { iso2: 'TG', name: 'Togo', dial: '228' }, // NOTE: on blocklist at launch — filtered out
  { iso2: 'TN', name: 'Tunisia', dial: '216' }, // NOTE: on blocklist at launch — filtered out
  { iso2: 'TZ', name: 'Tanzania', dial: '255' },
  { iso2: 'UG', name: 'Uganda', dial: '256' },
  { iso2: 'ZA', name: 'South Africa', dial: '27' },
  { iso2: 'ZM', name: 'Zambia', dial: '260' },
  { iso2: 'ZW', name: 'Zimbabwe', dial: '263' },
  // South Atlantic
  { iso2: 'FK', name: 'Falkland Islands', dial: '500' },
  { iso2: 'SH', name: 'Saint Helena', dial: '290' },
];

// CloudFront can return ISO-2 codes we don't want cluttering the
// picker (uninhabited / near-uninhabited territories, disputed
// regions). Map them to a reasonable parent so `resolveToKnownCountry`
// still preselects something sensible. The chip then shows the
// parent's flag + dial — which is what users from those places would
// actually want to enter anyway.
const TERRITORY_FALLBACK: Readonly<Record<string, string>> = {
  AQ: 'US', // Antarctica — no meaningful default; US is our universal fallback
  BV: 'NO', // Bouvet Island → Norway
  CC: 'AU', // Cocos (Keeling) Islands → Australia
  CX: 'AU', // Christmas Island → Australia
  EH: 'MA', // Western Sahara → Morocco
  GS: 'FK', // South Georgia → Falklands
  HM: 'AU', // Heard & McDonald Islands → Australia
  IO: 'GB', // British Indian Ocean Territory → UK
  NF: 'AU', // Norfolk Island → Australia
  NU: 'NZ', // Niue → New Zealand
  PN: 'NZ', // Pitcairn → New Zealand
  SJ: 'NO', // Svalbard and Jan Mayen → Norway
  TF: 'RE', // French Southern Territories → Réunion (same +262)
  TK: 'NZ', // Tokelau → New Zealand
  UM: 'US', // US Minor Outlying Islands → US
};

// Apply the blocked-prefix filter at module-load time.
export const COUNTRIES: ReadonlyArray<Country> = SEED.filter(
  (c) => !BLOCKED_DIAL_PREFIXES.has(c.dial),
);

const ISO2_SET: ReadonlySet<string> = new Set(COUNTRIES.map((c) => c.iso2));

export function isKnownCountry(iso2: string | null | undefined): boolean {
  if (!iso2) return false;
  return ISO2_SET.has(iso2.toUpperCase());
}

/**
 * Given any ISO-2 (including territory codes CloudFront might return),
 * return the ISO-2 we actually want to preselect — or null if the
 * code is completely unknown and has no fallback. Direct hits in
 * COUNTRIES win; TERRITORY_FALLBACK is the safety net.
 */
export function resolveToKnownCountry(iso2: string | null | undefined): string | null {
  if (!iso2) return null;
  const upper = iso2.toUpperCase();
  if (ISO2_SET.has(upper)) return upper;
  const fallback = TERRITORY_FALLBACK[upper];
  if (fallback && ISO2_SET.has(fallback)) return fallback;
  return null;
}

export function findCountryByIso2(iso2: string): Country | undefined {
  const upper = iso2.toUpperCase();
  return COUNTRIES.find((c) => c.iso2 === upper);
}

/**
 * Parse an E.164-ish string (`+34612345678`) and return the
 * matching country. Longest-prefix match so `+1` picks a specific
 * North-American entry (the component lets the user override).
 * Returns null when no known country matches.
 */
export function findCountryByDialPrefix(e164: string): Country | null {
  if (!e164.startsWith('+')) return null;
  const digits = e164.slice(1).replace(/\D/g, '');
  if (!digits) return null;
  // Sort by dial length descending so we try 4-digit codes before
  // 3-digit, 3 before 2, etc.
  const bestMatch = [...COUNTRIES]
    .sort((a, b) => b.dial.length - a.dial.length)
    .find((c) => digits.startsWith(c.dial));
  return bestMatch ?? null;
}

/**
 * Convert ISO-2 to its Unicode flag emoji via regional-indicator
 * code points. `'ES'` → `'🇪🇸'`. Non-ASCII-safe but browsers that
 * can't render flag glyphs (Windows without emoji fonts) show
 * empty boxes — the surrounding UI always shows the ISO-2 text
 * so the fallback is readable.
 */
export function flagEmoji(iso2: string): string {
  if (iso2.length !== 2) return '';
  const base = 0x1f1e6 - 'A'.charCodeAt(0);
  const first = iso2.charCodeAt(0);
  const second = iso2.charCodeAt(1);
  if (first < 65 || first > 90 || second < 65 || second > 90) return '';
  return String.fromCodePoint(base + first, base + second);
}

/**
 * Localised country name via Intl.DisplayNames with a graceful
 * fallback to the English name baked into the table. Caller should
 * pass the i18n locale (useTranslation().i18n.language).
 */
export function countryName(iso2: string, locale = 'en'): string {
  try {
    const dn = new Intl.DisplayNames([locale], { type: 'region' });
    const name = dn.of(iso2);
    if (name && name !== iso2) return name;
  } catch {
    /* older browsers — fall through */
  }
  return findCountryByIso2(iso2)?.name ?? iso2;
}
